import {
  Badge,
  Box,
  Button,
  CellProps,
  Center,
  FiExternalLink,
  Flex,
  HintIcon,
  IconButton,
  Image,
  Modal,
  Table,
  TableInstance,
  TableWrapper,
  Text,
  Tooltip,
  useDisclosure,
  usePagination,
  useTable,
  useTheme,
} from '@agrotoken-tech/ui';
import { Pagination } from '@bcba/frontend-utils';
import { useTranslation } from '@bcba/react-hooks';
import { ExplorerSearchDigitalDossierSerializer } from '@bcba/serializers';
import { LocaleKeys } from '@bcba/translations';
import { SEARCH_DIGITAL_DOSSIER_ACTIONS } from '@bcba/types';
import { useMutation } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useUserContext } from '../../contexts/UserContext';
import { paths } from '../../routes/paths';
import { DigitalDossierSearchResult } from '../../services/explorer/getDigitalDossierSearch';
import { postCreateAccessRequest } from '../../services/explorer/postCreateAccessRequest';
import confirmationArt from './assets/confirmation-art.jpg';

interface Props {
  data: DigitalDossierSearchResult;
  setPage: Dispatch<SetStateAction<number>>;
  refetch: () => void;
}
export const ExplorerResultsTable = ({ data, setPage, refetch }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    isOpen: isOpenSuccessModal,
    onOpen: onOpenSuccessModal,
    onClose: onCloseSuccessModal,
  } = useDisclosure();

  const { user, refetchUser } = useUserContext();
  const hasBeenApprovedDigitalDossier = user.hasDossierUuidsDataset;

  const { mutate: postAccessRequest, isPending: isLoadingAccessRequest } =
    useMutation({
      mutationFn: (companyUuid: string) =>
        postCreateAccessRequest({ uuid: companyUuid }),
      onSuccess: () => {
        refetch();
        refetchUser();
        onOpenSuccessModal();
      },
    });

  const columns = useMemo(
    () => [
      {
        Header: t('common.CUIT'),
        accessor: 'fiscalId',
      },
      {
        Header: t('accessRequests.table.companyName'),
        accessor: 'companyName',
      },
      {
        Header: t('receivedAccessRequests.table.requestStatus'),
        accessor: 'status',
        Cell: (props: CellProps<ExplorerSearchDigitalDossierSerializer>) => {
          const cellRelatedProps = props.row.original;
          const { status } = cellRelatedProps || {};
          // TODO: importing STATUS from @types enum throws TS compilation error (fix then use enum here)
          const isPendingStatus = status === ('PENDING' as const);

          return (
            <Badge colorScheme={isPendingStatus ? 'gray' : 'success'}>
              {t(`explorer.digitalDossier.explorer.status.${status}`)}
            </Badge>
          );
        },
      },
      {
        Header: t('receivedAccessRequests.table.actions'),
        accessor: 'action',
        Cell: (props: CellProps<ExplorerSearchDigitalDossierSerializer>) => {
          const cellRelatedProps = props.row.original;
          const { companyUuid, action, actionIsEnabled, actionMessage } =
            cellRelatedProps || {};
          const digitalDossierDetailUrl = paths.sharedDigitalDossier.replace(
            ':uuid',
            companyUuid,
          );
          const isViewAction = action === SEARCH_DIGITAL_DOSSIER_ACTIONS.VIEW;
          const isRequestAccessAction =
            action === SEARCH_DIGITAL_DOSSIER_ACTIONS.REQUEST_ACCESS;
          const enableButton = actionIsEnabled && hasBeenApprovedDigitalDossier;
          const tooltipMessage = actionMessage
            ? t(actionMessage as LocaleKeys)
            : t(
                'explorer.digitalDossier.explorer.action.tooltip.hasntBeenApprovedDigitalDossier',
              );

          if (isViewAction) {
            return (
              <Button
                display={'flex'}
                justifyContent={'start'}
                as={Link}
                to={digitalDossierDetailUrl}
                variant="linkSecondary"
                lineHeight={'20px'}
                sx={{
                  span: {
                    h: '16px',
                    w: '16px',
                    svg: {
                      h: '16px',
                      w: '16px',
                    },
                  },
                }}
                leftIcon={<FiExternalLink />}
                size={'sm'}
              >
                {t('explorer.digitalDossier.explorer.action.view')}
              </Button>
            );
          }

          if (isRequestAccessAction) {
            return (
              <Flex w="full" gap="2">
                <Button
                  w="fit-content"
                  display={'inline-flex'}
                  isDisabled={!enableButton}
                  variant="link"
                  isLoading={isLoadingAccessRequest}
                  onClick={() => postAccessRequest(companyUuid)}
                >
                  {t('explorer.digitalDossier.explorer.action.requestAccess')}
                </Button>
                {actionMessage || !hasBeenApprovedDigitalDossier ? (
                  <Tooltip
                    hasArrow
                    label={t(tooltipMessage as LocaleKeys)}
                    placement="top"
                  >
                    <IconButton
                      h="16px"
                      w="16px !important"
                      minWidth="16px"
                      icon={<HintIcon />}
                      _hover={{}}
                      aria-label=""
                      variant={'ghost'}
                    ></IconButton>
                  </Tooltip>
                ) : null}
              </Flex>
            );
          }

          return '';
        },
      },
    ],
    [isLoadingAccessRequest],
  );

  const tableInstance = useTable(
    {
      columns: columns as any,
      data: data.data,
    },
    usePagination,
  );

  return (
    <>
      <Flex
        w="full"
        gap="6"
        flexDir={'column'}
        sx={{
          // tune some styles to match figma
          '.chakra-table__container > div:first-of-type': { display: 'none' },
          table: { borderTop: 0 },
        }}
      >
        <Text size="md">
          {t('explorer.digitalDossier.explorer.resultsTitle')}
        </Text>
        <TableWrapper>
          <Table
            tableInstance={tableInstance as TableInstance<object>}
            isLoading={false}
            emptyStateContent={{
              title: t('explorer.digitalDossier.explorer.noResultsTitle'),
              description: t(
                'explorer.digitalDossier.explorer.noResultsSubtitle',
              ),
            }}
          ></Table>
        </TableWrapper>
        <Box my="3">
          <Pagination
            currentPage={data?.paginationData.currentPage!}
            totalPageCount={data?.paginationData.totalPages!}
            previousButtonLabel={t('common.previous')}
            nextButtonLabel={t('common.next')}
            onPageChange={setPage}
            theme={theme}
          />
        </Box>
      </Flex>
      <Modal
        size="lg"
        hideModalCloseButton
        isOpen={isOpenSuccessModal}
        onClose={onCloseSuccessModal}
      >
        <Center w="full" flexDir={'column'} mb="-32px" mt="-12px">
          <Image src={confirmationArt} alt="" h="114.54px" w="114.54px" />
          <Text size="lg" variant="medium" mt="20px" mb="8px">
            {t('accessRequests.successModal.title')}
          </Text>
          <Text textAlign={'center'} mb="32px">
            {t('accessRequests.successModal.description')}
          </Text>
          <Button variant={'primary'} onClick={onCloseSuccessModal}>
            {t('accessRequests.successModal.confirm')}
          </Button>
        </Center>
      </Modal>
    </>
  );
};
